import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { BeispielEtikett } from "../../../components/Beispiel";
import { useStaffWorkspace } from "../../../app/session";
import { ErrorState, Loading, NoAccess } from "../../../components/States";
import { KopfMehr, MailAdresse, Money, PillView, SortTh, Toolbar, matchesSearch, useSorted, SortWahl } from "../../../components/ui";
import { Icon } from "../../../components/Icon";
import { money, monthLabel } from "../../../lib/format";
import { BrandDialog } from "./BrandDialog";
import { useBase, useBrandContacts } from "./hooks";
import { brandLines, matchesBrandFilter, ohneKursText, pill, sumsLabel, type BrandFilter, type BrandFull } from "./model";
import { DemoNote, HIDDEN_FINANCIALS, useSicht } from "./parts";
import { t, ta } from "../../../lib/sprache";
import { VoreinstellungenEintrag } from "../einstellungen/Voreinstellungen";
import { NurLesenHinweis, NurSchreiben, useNurLesen } from "../../../app/nurLesen";

/** Marken-Liste (11_app_agentur/marken-liste.html) mit Umfang des Altportals (AD-082 … AD-086). */

const FILTERS: { key: BrandFilter; label: string }[] = [
  { key: "alle", get label() { return t("Alle", "All"); } },
  { key: "aktiv", get label() { return t("Aktiv", "Active"); } },
  { key: "ueberfaellig", get label() { return t("Überfällig", "Overdue"); } },
  { key: "ohne", get label() { return t("Ohne Kooperation", "Without a deal"); } },
  { key: "agenturen", get label() { return t("Agenturen", "Agencies"); } },
];

export function MarkenListe() {
  const ws = useStaffWorkspace();
  // K-1/K-2: Pause und Nur-Lesen
  const nurLesen = useNurLesen();
  const navigate = useNavigate();
  const sicht = useSicht();
  const { base, error, loading, noAccess, retry, refresh } = useBase(ws);
  const contacts = useBrandContacts(ws.org.org_id);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<BrandFilter>("alle");
  // Startleitfaden: „Marke anlegen“ öffnet das Formular gleich (/agentur/marken?neu=1)
  const [params, setParams] = useSearchParams();
  const [dialog, setDialog] = useState<{ brand: BrandFull | null } | null>(() => (params.get("neu") === "1" ? { brand: null } : null));
  useEffect(() => {
    if (params.get("neu") !== "1") return;
    const next = new URLSearchParams(params);
    next.delete("neu");
    setParams(next, { replace: true });
  }, []);

  const lines = useMemo(() => (base ? brandLines(base, contacts.data ?? []) : []), [base, contacts.data]);
  const shown = lines.filter((l) =>
    matchesBrandFilter(l, filter)
    && matchesSearch(`${l.brand.name} ${l.brand.legal_name ?? ""} ${l.contact ?? ""} ${l.brand.city ?? ""} ${l.agencyName ?? ""} ${l.statusPill.label}`, search));
  const { sorted, sort, toggle } = useSorted(shown, {
    name: (l) => l.brand.name,
    kontakt: (l) => l.contact,
    status: (l) => l.statusPill.label,
    kooperationen: (l) => l.deals,
    letzte: (l) => l.last,
    offen: (l) => l.open.sums.reduce((a, s) => a + s.cents, 0),
    umsatz: (l) => l.yearGross,
  });

  const err = error ?? contacts.error;
  if (err) return <ErrorState error={err} retry={() => { retry(); void contacts.refetch(); }} />;
  if (noAccess) return <NoAccess />;
  if (loading || !base || contacts.isPending) return <Loading />;
  const year = base.today.slice(0, 4);

  return (
    <main id="main" className="content tk-sd tk-sd-liste tk-sd-marken">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")}</div>
          <h1>{t("Marken", "Brands")}</h1>
          <p>{t("Deine Kunden: Firma, Ansprechpartner, Rechnungsadresse und was mit ihnen läuft.", "Your customers: company, contacts, billing address and what is running with them.")}</p>
        </div>
        <div className="row tk-kopf-aktionen">
          {/* D-2: eine Hauptaktion, Seltenes hinter „Mehr“ */}
          <button className="primary" data-schreiben data-coco="marke-anlegen" onClick={() => setDialog({ brand: null })}>{t("Marke anlegen", "Add brand")}</button>
          <KopfMehr>
            <NurSchreiben><Link to="/agentur/marken/import">{t("Importieren", "Import")}</Link></NurSchreiben>
            <VoreinstellungenEintrag modul="marken" />
          </KopfMehr>
        </div>
      </div>

      <Toolbar
        search={search}
        onSearch={setSearch}
        label={t("Liste durchsuchen", "Search list")}
        placeholder={t("Suchen …", "Search …")}
        filters={FILTERS.map((f) => ({ ...f, count: lines.filter((l) => matchesBrandFilter(l, f.key)).length }))}
        filter={filter}
        onFilter={setFilter}
      >
        <button className="tk-sd-neu-laden" onClick={() => { refresh(); void contacts.refetch(); }}>{t("Aktualisieren", "Refresh")}</button>
      </Toolbar>

      <SortWahl />
      <div className="tablewrap tk-sticky-end tk-koop-karten">
        <table>
          <caption className="sr-only">{t("Marken", "Brands")}</caption>
          <thead>
            <tr>
              <SortTh label={t("Marke", "Brand")} k="name" sort={sort} toggle={toggle} />
              <SortTh label={t("Kontakt", "Contact")} k="kontakt" sort={sort} toggle={toggle} className="tk-sd-c-mail" />
              <SortTh label="Status" k="status" sort={sort} toggle={toggle} />
              <SortTh label={t("Kooperationen", "Deals")} k="kooperationen" sort={sort} toggle={toggle} money />
              <SortTh label={t("Letzte Kooperation", "Last deal")} k="letzte" sort={sort} toggle={toggle} className="tk-sd-c-letzte" />
              {sicht.amounts && <SortTh label={t("Offen brutto", "Open gross")} k="offen" sort={sort} toggle={toggle} money />}
              {sicht.financials && <SortTh label={t("Umsatz netto", "Net revenue")} k="umsatz" sort={sort} toggle={toggle} money className="tk-sd-c-umsatz" />}
              <th scope="col"><span className="sr-only">{t("Aktionen", "Actions")}</span></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((l) => {
              const zusatz = [l.brand.legal_name, l.agencyName ? t(`über ${l.agencyName}${l.brand.invoice_via_agency ? t(" · Rechnung an die Agentur", " · invoice to the agency") : ""}`, `via ${l.agencyName}${l.brand.invoice_via_agency ? t(" · Rechnung an die Agentur", " · invoice to the agency") : ""}`) : null].filter(Boolean).join(" · ");
              const viaMarken = l.via && l.via.brands > 0 ? (l.via.brands === 1 ? t("1 betreute Marke", "1 managed brand") : t(`${l.via.brands} betreute Marken`, `${l.via.brands} managed brands`)) : "";
              const ueber = l.via && viaMarken ? t(`über ${viaMarken}: ${l.via.deals} · ${l.via.activeDeals} aktiv`, `over ${viaMarken}: ${l.via.deals} · ${l.via.activeDeals} active`) : "";
              return (
              <tr key={l.brand.id} className="tk-sd-zeile" onClick={(e) => {
                // D-1: die ganze Zeile öffnet die Marke; Knöpfe und Links behalten ihre eigene Wirkung
                const el = e.target as HTMLElement;
                if (el.closest("a") || el.closest("button") || el.closest("input")) return;
                navigate(`/agentur/marken/${l.brand.id}`);
              }}>
                {/* lange Markennamen brechen um, damit die Kontaktadresse ganz in eine Zeile passt (ab 1100 px einzeilig mit Tooltip) */}
                <td className="tk-koop-kopf tk-text-umbruch">
                  <Link to={`/agentur/marken/${l.brand.id}`} title={l.brand.name}><strong>{l.brand.name}</strong></Link><BeispielEtikett von={l.brand} />
                  <small title={zusatz || undefined}>{zusatz || " "}</small>
                  {/* D2-1: eingeklappte Spalte „Kontakt“ (schmale Liste) */}
                  {l.contact && <small className="tk-sd-alt" title={l.contact}>{l.contact}</small>}
                </td>
                <td className="tk-adresse tk-sd-c-mail">{l.contact ? <MailAdresse adresse={l.contact} /> : "–"}</td>
                <td data-label={t("Status", "Status")}>
                  <span className="tk-pills">
                    <PillView pill={l.statusPill} />
                    {l.overdue && <PillView pill={pill(l.overdue.count === 1 ? t("1 überfällig", "1 overdue") : t(`${l.overdue.count} überfällig`, `${l.overdue.count} overdue`), "error")} />}
                  </span>
                </td>
                <td data-label={t("Kooperationen", "Deals")}>
                  <span className="num">{l.deals}</span>{l.activeDeals > 0 && <small>{l.activeDeals} {t("aktiv", "active")}</small>}
                  {ueber && <small className="tk-sd-ueber" title={ueber}>{ueber}</small>}
                </td>
                <td data-label={t("Letzte Kooperation", "Last deal")} className="tk-sd-c-letzte">{l.last ? monthLabel(l.last) : "–"}</td>
                {sicht.amounts && (
                  <td data-label={t("Offen brutto", "Open gross")}>
                    {l.open.count ? <span className="num">{sumsLabel(l.open.sums, money)}</span> : <span className="num">–</span>}
                    {l.via && l.via.open.count > 0 && <small>{t("betreute Marken: ", "managed brands: ")}{sumsLabel(l.via.open.sums, money)}</small>}
                  </td>
                )}
                {sicht.financials && <td data-label={t("Umsatz netto", "Net revenue")} className="tk-sd-c-umsatz"><Money cents={l.yearGross} currency={base.currency} /></td>}
                <td className="tk-koop-aktion"><button className="tk-icon-btn" onClick={() => setDialog({ brand: l.brand })} aria-label={t(`${l.brand.name} bearbeiten`, `Edit ${l.brand.name}`)} title={t("Bearbeiten", "Edit")}><Icon name="edit" /></button></td>
              </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={6 + Number(sicht.amounts) + Number(sicht.financials)}>
                  {search || filter !== "alle"
                    ? t("Keine passenden Ergebnisse. Passe die Suche oder die Filter an.", "No matching results. Adjust the search or the filters.")
                    : nurLesen ? <>{t("Noch keine Marken.", "No brands yet.")}<NurLesenHinweis /></>
                    : t("Noch keine Marken. Lege die erste Marke an; danach kannst du Kooperationen zuordnen.", "No brands yet. Create the first brand; then you can assign deals.")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="source-note">
        {sorted.length === 1 ? t("1 Eintrag", "1 entry") : t(`${sorted.length} Einträge`, `${sorted.length} entries`)} · {sicht.financials
          ? ta(`Umsatz netto ist das Kooperationsvolumen ${year} (Budget, Zusatzpositionen und Agenturgebühren, verbucht und geplant), gerechnet wie auf der Übersicht.`, `Net revenue is the deal volume ${year} (budget, extra items and agency fees, booked and planned), calculated as in the overview.`, `Umsatz netto ist das Kooperationsvolumen ${year} (Budget und Zusatzpositionen, verbucht und geplant), gerechnet wie auf der Übersicht.`, `Net revenue is the deal volume ${year} (budget and extra items, booked and planned), calculated as in the overview.`)
          : HIDDEN_FINANCIALS()}
        {sicht.amounts ? t(" Offen brutto sind die noch nicht bezahlten Rechnungen an die Marke.", " Open gross are the unpaid invoices to the brand.") : ""} {t("Bei Agenturen stehen die Zahlen der betreuten Marken getrennt darunter; sie zählen bei den Marken selbst.", "For agencies, the figures of the managed brands are listed separately below; they count for the brands themselves.")}{ws.isChef || ws.membership.sees_all_talents ? "" : t(" Umsätze und Rechnungen zeigen nur deine zugewiesenen Creator.", " Revenue and invoices only show your assigned creators.")}
        {base.unconverted > 0 && sicht.financials && ` ${ohneKursText(base.unconverted)}`}
      </p>
      <DemoNote />
      {dialog && <BrandDialog brand={dialog.brand} base={base} contacts={contacts.data ?? []} onClose={() => setDialog(null)} />}
    </main>
  );
}
